import { Box } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser } from './Scheduler.data'
import {
  FONT_SANS,
  PillButton,
  SchDisplay,
  SchedulerThemeScope,
  TOKENS,
} from './SchedulerDesign'

/**
 * Wraps every `/plan-scheduler` route. When `/me` says the user is licensed on
 * this tenant but has no `scheduler` role (`schedulerAccess: false`), shows
 * why instead of the pages, whose every request would come back 403.
 *
 * Only an explicit `false` blocks. Loading, an error or an older service that
 * does not send the field all render the pages as before, for the same reason
 * `usePermissions` is optimistic: a slow /me must not look like a lock-out.
 */
export const SchedulerAccessGate = ({ children }: { children: ReactNode }) => {
  const { data } = useCurrentUser()
  const navigate = useNavigate()
  if (data?.schedulerAccess !== false) return <>{children}</>

  return (
    <SchedulerThemeScope>
      <Box
        sx={{
          maxWidth: 560,
          mx: 'auto',
          mt: 8,
          px: 2,
          fontFamily: FONT_SANS,
          color: TOKENS.ink900,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          <LockOutlinedIcon sx={{ fontSize: 20, color: TOKENS.brandTeal }} />
          <SchDisplay>No access to the scheduler</SchDisplay>
        </Box>
        <Box sx={{ fontSize: 13, color: TOKENS.ink600, lineHeight: 1.55 }}>
          You are signed in as <b>{data.username}</b>, but your user on{' '}
          <b>{data.tenant ?? 'this tenant'}</b> does not have the scheduler
          role. Ask an administrator to add it in the planner&apos;s user
          administration, then reload this page.
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <PillButton
            kind="secondary"
            onClick={() => navigate('/select-tenant')}
          >
            Switch tenant
          </PillButton>
        </Box>
      </Box>
    </SchedulerThemeScope>
  )
}
